import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef } from 'react'

import type { QueryClient } from '@tanstack/react-query'
import type {
  FirstDataRenderedEvent,
  GetRowIdParams,
  GridApi,
  GridReadyEvent,
  IServerSideGetRowsParams,
} from 'ag-grid-enterprise'
import type { AgGridReact } from 'ag-grid-react'
import type { ChangeEvent, ReactElement } from 'react'
import type { IIapContext } from '~/providers/Iap'
import type { IInApp, IInAppWithAppIconUrl } from '~/types/iap'

import columns from './columns'

import Filters from '../../InApps/Filters'

import { getAppsManagerList } from '~/api/application-management'
import { getInAppsList } from '~/api/iap'
import Button from '~/components/Buttons/Button'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import ServerSideTableWrapper from '~/components/Tables/ServerSideTableWrapper'
import { dataNotAvailableText } from '~/const'
import { FASTLY_PATH } from '~/const/asset'
import { useIapContext } from '~/providers/Iap'
import { keys as appsKeys } from '~/queries/application-management'
import { iapKeys } from '~/queries/iap'
import { debounce } from '~/utils/function'
import { resize } from '~/utils/media'

import './styles.scss'

const MODAL_ID = 'add-in-app-modal'

const CACHE_BLOCK_SIZE = 30

const ENTRIES_NAME = 'in-apps-list-editor-modal'

const getDatasource = ({
  context,
  gridApi,
  queryClient,
  search,
}: {
  context: IIapContext
  gridApi?: GridApi
  queryClient: QueryClient
  search: string
}) => ({
  getRows: async (params: IServerSideGetRowsParams) => {
    const { fail, request, success } = params
    const { state } = context
    const { apps, freeTrial, period, priceRange, type } = state

    gridApi?.hideOverlay()

    try {
      const currentPage = (request.startRow || 0) / CACHE_BLOCK_SIZE

      const queryParams = {
        apps,
        freeTrial,
        limit: CACHE_BLOCK_SIZE,
        page: currentPage,
        period,
        priceRange,
        searchQuery: search,
        type,
      }

      const appsList = await queryClient.fetchQuery({
        queryFn: getAppsManagerList,
        queryKey: appsKeys.list(),
      })

      const { data: inAppsList } = await queryClient.fetchQuery({
        queryFn: () => getInAppsList(queryParams),
        queryKey: iapKeys.inAppsList(queryParams),
      })

      const rowData =
        inAppsList?.map((item) => {
          const app = appsList?.find((app) => app.app_key === item.app_key)
          return {
            ...item,
            app_icon_url: app?.icon_file_id ? resize(`${FASTLY_PATH}${app.icon_file_id}`, 50, 50) : '',
          }
        }) || []

      if (!rowData.length) {
        gridApi?.showNoRowsOverlay()
      }

      success({ rowData })
    } catch (error) {
      captureException(error)
      fail()
    }
  },
})

interface Props {
  isOpen: boolean
  onAddClick: (values: IInAppWithAppIconUrl[]) => void
  onClose: VoidFunction
  selectedInApps: IInAppWithAppIconUrl[]
}

const OfferEditorInAppsModal = ({ isOpen, onAddClick, onClose, selectedInApps }: Props): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const context = useIapContext()

  const gridRef = useRef<AgGridReact>(null)

  const tableRef = useRef<HTMLDivElement>(null)

  const debouncedSearch = debounce((search) => {
    const datasource = getDatasource({ context, gridApi: gridRef.current?.api, queryClient, search })
    gridRef.current?.api?.setGridOption('serverSideDatasource', datasource)
  }, 500)

  /** Effects */

  useEffect(() => {
    if (context.state) {
      const datasource = getDatasource({ context, gridApi: gridRef.current?.api, queryClient, search: '' })
      gridRef.current?.api?.setGridOption('serverSideDatasource', datasource)
    }
  }, [context, queryClient])

  /** Handlers */

  const handleGridReady = useCallback(
    (evt: GridReadyEvent) => {
      if (gridRef.current?.api) {
        const datasource = getDatasource({ context, gridApi: gridRef.current?.api, queryClient, search: '' })
        evt.api.setGridOption('serverSideDatasource', datasource)
      }
    },
    [context, queryClient]
  )

  const handleFirstDataRendered = useCallback(
    (event: FirstDataRenderedEvent<IInAppWithAppIconUrl>) =>
      event.api.forEachNode((node) => node.setSelected(selectedInApps.some((inApp) => inApp.sku === node.data?.sku))),
    [selectedInApps]
  )

  const handleGetRowId = useCallback(
    ({ data }: GetRowIdParams<IInApp>) => `${data.app_key}-${data.sku}-${data.device_type}`,
    []
  )

  const handleSearchChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => debouncedSearch(evt.currentTarget.value),
    [debouncedSearch]
  )

  const handleAddClick = () => {
    const gridApi = gridRef.current?.api

    if (gridApi) {
      const selectedRows: IInAppWithAppIconUrl[] = gridApi.getSelectedRows()
      onAddClick(selectedRows)
    }
  }

  /** Render */

  return (
    <Modal className="in-apps-editor-modal" id={MODAL_ID} isOpen={isOpen} onCloseModal={onClose}>
      <ModalHeader>Add In-Apps</ModalHeader>
      <ModalBody>
        <div className="table-container">
          <Filters modalId={MODAL_ID} />
          <ServerSideTableWrapper
            cacheBlockSize={CACHE_BLOCK_SIZE}
            className="inapp-table"
            columnDefs={columns}
            entriesName={ENTRIES_NAME}
            getRowId={handleGetRowId}
            onFirstDataRendered={handleFirstDataRendered}
            onGridReady={handleGridReady}
            onSearchChange={handleSearchChange}
            overlayNoRowsTemplate={dataNotAvailableText}
            ref={gridRef}
            rowHeight={69}
            rowModelType="serverSide"
            rowSelection={{
              checkboxes: false,
              enableClickSelection: true,
              enableSelectionWithoutKeys: true,
              headerCheckbox: false,
              mode: 'multiRow',
            }}
            suppressCsvExport
            suppressExcelExport
            tableRef={tableRef}
            withPagination={false}
            withSearchBar
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <Button ghost onClick={onClose} type="button">
          Cancel
        </Button>
        <Button onClick={handleAddClick}>Add</Button>
      </ModalFooter>
    </Modal>
  )
}

export default OfferEditorInAppsModal
